import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { UserStatus, Company } from '../../types/auth';
import { StatusBadge } from '../../components/common/StatusBadge';
import { api } from '../../services/api';
import { showError } from '../../utils/notification';

interface ModerationApplication {
    id: string;
    submittedDate: string;
    status: UserStatus;
    moderatorComments?: string;
    rejectionReason?: string;
    lastUpdated: string;
    estimatedProcessingTime: string;
    documents: ApplicationDocument[];
    companyInfo: CompanyInfo;
}

interface ApplicationDocument {
    id: string;
    type: string;
    name: string;
    status: 'pending' | 'approved' | 'rejected';
    uploadDate: string;
    rejectionReason?: string;
    size: number;
}

interface CompanyInfo {
    name: string;
    bin: string;
    address: string;
    contactPerson: string;
    phone: string;
    email: string;
}

export const ModerationPage: React.FC = () => {
    const { user, refreshUser } = useAuth();
    const [application, setApplication] = useState<ModerationApplication | null>(null);
    const [company, setCompany] = useState<Company | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [isResetting, setIsResetting] = useState(false);

    // Загрузка данных заявки на модерацию
    useEffect(() => {
        const loadData = async () => {
            if (!user) return;

            try {
                // Загружаем данные компании если есть companyId
                if (user.companyId) {
                    try {
                        const companyResponse = await api.getMyCompany();
                        if (companyResponse.data.success && companyResponse.data.data) {
                            setCompany(companyResponse.data.data);
                        }
                    } catch (error) {
                        console.log('Компания не найдена или ошибка загрузки:', error);
                    }
                }

            } catch (error) {
                console.error('Ошибка загрузки данных:', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadData();
    }, [user?.companyId]);

    // Создание данных заявки на модерацию
    useEffect(() => {
        if (!user) return;

        const mockApplication: ModerationApplication = {
            id: `APP-${user.id}`,
            submittedDate: '2024-01-15T10:30:00Z',
            status: user.status,
            moderatorComments: user.status === UserStatus.NEEDS_REVISION ?
                user.moderatorComments || 'Требуется доработка документов.' :
                undefined,
            rejectionReason: user.rejectionReason,
            lastUpdated: '2024-01-16T14:22:00Z',
            estimatedProcessingTime: getEstimatedTime(user.status),
            documents: [
                {
                    id: '1',
                    type: 'company_registration',
                    name: 'Справка о государственной регистрации',
                    status: user.status === UserStatus.NEEDS_REVISION ? 'rejected' : 'pending',
                    uploadDate: '2024-01-15T10:30:00Z',
                    rejectionReason: user.status === UserStatus.NEEDS_REVISION ? 'Документ нечеткий, требуется лучшее качество сканирования' : undefined,
                    size: 2048000
                },
                {
                    id: '2',
                    type: 'tax_certificate',
                    name: 'Справка об отсутствии налоговой задолженности',
                    status: user.status === UserStatus.NEEDS_REVISION ? 'rejected' : 'pending',
                    uploadDate: '2024-01-15T10:32:00Z',
                    rejectionReason: user.status === UserStatus.NEEDS_REVISION ? 'Документ просрочен, требуется актуальная справка' : undefined,
                    size: 1536000
                },
                {
                    id: '3',
                    type: 'bank_details',
                    name: 'Справка о банковских реквизитах',
                    status: 'approved',
                    uploadDate: '2024-01-15T10:35:00Z',
                    size: 1024000
                }
            ],
            companyInfo: {
                name: company?.name || 'ТОО "Пример"',
                bin: company?.bin || '123456789012',
                address: company?.legalAddress || 'г. Алматы, ул. Примерная, 123',
                contactPerson: company?.directorName || user.fullName,
                phone: company?.contactPhone || '+7 777 123 45 67',
                email: company?.contactEmail || user.login
            }
        };
        setApplication(mockApplication);
        setIsLoading(false);
    }, [user, company]);

    const getEstimatedTime = (status: UserStatus): string => {
        switch (status) {
            case UserStatus.PENDING_MODERATION:
                return '1-2 рабочих дня';
            case UserStatus.NEEDS_REVISION:
                return 'После исправления: 1 рабочий день';
            case UserStatus.REJECTED:
                return 'Подача новой заявки: 1-2 рабочих дня';
            default:
                return 'Завершено';
        }
    };

    const getStatusIcon = (status: UserStatus) => {
        switch (status) {
            case UserStatus.PENDING_MODERATION:
                return (
                    <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                );
            case UserStatus.NEEDS_REVISION:
                return (
                    <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                        </svg>
                    </div>
                );
            case UserStatus.APPROVED:
                return (
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                );
            case UserStatus.REJECTED:
                return (
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </div>
                );
            default:
                return (
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                        <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                );
        }
    };

    const getDocumentStatusColor = (status: ApplicationDocument['status']) => {
        switch (status) {
            case 'approved':
                return 'bg-green-100 text-green-700 border-green-300';
            case 'rejected':
                return 'bg-red-100 text-red-700 border-red-300';
            default:
                return 'bg-yellow-100 text-yellow-700 border-yellow-300';
        }
    };

    const getDocumentStatusText = (status: ApplicationDocument['status']) => {
        switch (status) {
            case 'approved':
                return 'Одобрен';
            case 'rejected':
                return 'Отклонен';
            default:
                return 'На рассмотрении';
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatFileSize = (bytes: number) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            await refreshUser();

            // Перезагружаем данные компании
            if (user?.companyId) {
                try {
                    const companyResponse = await api.getMyCompany();
                    if (companyResponse.data.success && companyResponse.data.data) {
                        setCompany(companyResponse.data.data);
                    }
                } catch (error) {
                    console.log('Ошибка обновления данных компании:', error);
                }
            }

            setIsRefreshing(false);
        } catch (error) {
            console.error('Ошибка при обновлении данных:', error);
            setIsRefreshing(false);
        }
    };

    const handleResetApplication = async () => {
        setIsResetting(true);
        try {
            const response = await api.resetApplication();
            console.log('Reset application response:', response);

            if (response.data && response.data.success) {
                // Обновляем данные пользователя
                await refreshUser();
                // Перенаправляем на страницу регистрации
                window.location.href = '/registration';
            } else {
                const message = response.data?.message || 'Неизвестная ошибка';
                showError('Ошибка', message);
            }
        } catch (error: any) {
            console.error('Error resetting application:', error);

            // Более детальная обработка ошибок
            let errorMessage = 'Произошла ошибка при сбросе заявки. Попробуйте позже.';

            if (error.message && error.message.includes('HTTP 403')) {
                // Токен истек или нет доступа
                errorMessage = 'Сессия истекла. Пожалуйста, войдите в систему заново.';
                // Очищаем токены и перенаправляем на логин
                localStorage.removeItem('accessToken');
                localStorage.removeItem('refreshToken');
                localStorage.removeItem('user');
                setTimeout(() => {
                    window.location.href = '/auth';
                }, 2000);
            } else if (error.response) {
                // Сервер ответил с кодом ошибки
                console.log('Error response:', error.response);
                errorMessage = `Ошибка ${error.response.status}: ${error.response.statusText}`;
            } else if (error.message) {
                errorMessage = error.message;
            }

            showError('Ошибка сброса заявки', errorMessage);
        } finally {
            setIsResetting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Загрузка данных заявки...</p>
                </div>
            </div>
        );
    }

    if (!application || !user) {
        return (
            <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Заявка не найдена</h3>
                <p className="text-gray-600">Не удалось загрузить данные заявки на модерацию</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Заявка на модерацию</h1>
                    <p className="text-gray-600 mt-1">Отслеживайте статус рассмотрения вашей заявки</p>
                </div>
                <button
                    onClick={handleRefresh}
                    disabled={isRefreshing}
                    className="flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                    <svg className={`w-4 h-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    {isRefreshing ? 'Обновление...' : 'Обновить'}
                </button>
            </div>

            {/* Status Overview */}
            <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm">
                <div className="flex items-center space-x-6">
                    {getStatusIcon(application.status)}
                    <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                            <h2 className="text-xl font-semibold text-gray-900">
                                Заявка #{application.id}
                            </h2>
                            <StatusBadge status={application.status} size="lg" />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                            <div>
                                <span className="font-medium">Подана:</span>
                                <br />
                                {formatDate(application.submittedDate)}
                            </div>
                            <div>
                                <span className="font-medium">Обновлена:</span>
                                <br />
                                {formatDate(application.lastUpdated)}
                            </div>
                            <div>
                                <span className="font-medium">Время обработки:</span>
                                <br />
                                {application.estimatedProcessingTime}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Status-specific messages */}
                {application.status === UserStatus.DRAFT && (
                    <div className="mt-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                        <div className="flex items-start space-x-3">
                            <svg className="w-5 h-5 text-gray-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <div>
                                <h3 className="text-sm font-medium text-gray-800">Заявка не подана</h3>
                                <p className="text-sm text-gray-700 mt-1">
                                    Для подачи заявки на модерацию необходимо завершить регистрацию компании и загрузить все необходимые документы.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {application.status === UserStatus.PENDING_MODERATION && (
                    <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="flex items-start space-x-3">
                            <svg className="w-5 h-5 text-yellow-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div>
                                <h3 className="text-sm font-medium text-yellow-800">Заявка на рассмотрении</h3>
                                <p className="text-sm text-yellow-700 mt-1">
                                    Ваша заявка находится в очереди на модерацию. Наши специалисты проверят предоставленные документы в течение 1-2 рабочих дней.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {application.status === UserStatus.NEEDS_REVISION && application.moderatorComments && (
                    <div className="mt-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                        <div className="flex items-start space-x-3">
                            <svg className="w-5 h-5 text-orange-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                            </svg>
                            <div>
                                <h3 className="text-sm font-medium text-orange-800">Требуется доработка</h3>
                                <p className="text-sm text-orange-700 mt-1">
                                    <strong>Комментарий модератора:</strong> {application.moderatorComments}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {application.status === UserStatus.APPROVED && (
                    <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-start space-x-3">
                            <svg className="w-5 h-5 text-green-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div>
                                <h3 className="text-sm font-medium text-green-800">Заявка одобрена!</h3>
                                <p className="text-sm text-green-700 mt-1">
                                    Поздравляем! Ваша заявка успешно прошла модерацию. Теперь вы можете пользоваться всеми функциями системы.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {application.status === UserStatus.VERIFIED && (
                    <div className="mt-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                        <div className="flex items-start space-x-3">
                            <svg className="w-5 h-5 text-emerald-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <div>
                                <h3 className="text-sm font-medium text-emerald-800">Аккаунт верифицирован</h3>
                                <p className="text-sm text-emerald-700 mt-1">
                                    Ваш аккаунт полностью верифицирован и готов к работе. Вы имеете доступ ко всем функциям системы.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {application.status === UserStatus.REJECTED && (
                    <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <div className="flex items-start space-x-3">
                            <svg className="w-5 h-5 text-red-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                            <div>
                                <h3 className="text-sm font-medium text-red-800">Заявка отклонена</h3>
                                <p className="text-sm text-red-700 mt-1">
                                    К сожалению, ваша заявка была отклонена модератором. Не расстраивайтесь — вы можете исправить недочеты и подать заявку повторно.
                                </p>
                                {application.rejectionReason && (
                                    <div className="mt-3 p-3 bg-white/70 backdrop-blur-sm rounded-lg border border-red-200">
                                        <h4 className="text-sm font-semibold text-red-800 mb-1 flex items-center">
                                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            Причина отклонения:
                                        </h4>
                                        <p className="text-red-700 text-sm leading-relaxed italic">
                                            "{application.rejectionReason}"
                                        </p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Company Information */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-6">Информация о компании</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                        <div>
                            <label className="text-sm font-medium text-gray-500">Название компании</label>
                            <p className="text-gray-900 font-medium">{application.companyInfo.name}</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-500">БИН</label>
                            <p className="text-gray-900 font-medium">{application.companyInfo.bin}</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-500">Адрес</label>
                            <p className="text-gray-900">{application.companyInfo.address}</p>
                        </div>
                    </div>
                    <div className="space-y-4">
                        <div>
                            <label className="text-sm font-medium text-gray-500">Контактное лицо</label>
                            <p className="text-gray-900 font-medium">{application.companyInfo.contactPerson}</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-500">Телефон</label>
                            <p className="text-gray-900">{application.companyInfo.phone}</p>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-500">Email</label>
                            <p className="text-gray-900">{application.companyInfo.email}</p>
                        </div>
                    </div>
                </div>
            </div>


            {/* Actions */}
            <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Действия</h3>
                <div className="flex flex-col sm:flex-row gap-4">
                    {application.status === UserStatus.DRAFT && (
                        <>
                            <button
                                onClick={() => window.location.href = '/registration'}
                                className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                            >
                                Завершить регистрацию
                            </button>
                            <button className="flex-1 bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 transition-colors font-medium">
                                Связаться с поддержкой
                            </button>
                        </>
                    )}

                    {application.status === UserStatus.NEEDS_REVISION && (
                        <>
                            <button
                                onClick={() => window.location.href = '/registration'}
                                className="flex-1 bg-orange-600 text-white px-6 py-3 rounded-lg hover:bg-orange-700 transition-colors font-medium"
                            >
                                Исправить документы
                            </button>
                            <button className="flex-1 bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 transition-colors font-medium">
                                Связаться с поддержкой
                            </button>
                        </>
                    )}

                    {application.status === UserStatus.REJECTED && (
                        <>
                            <button
                                onClick={handleResetApplication}
                                disabled={isResetting}
                                className="flex-1 bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                            >
                                {isResetting ? (
                                    <>
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Сброс заявки...
                                    </>
                                ) : (
                                    <>
                                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                        </svg>
                                        Подать новую заявку
                                    </>
                                )}
                            </button>
                            <button className="flex-1 bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 transition-colors font-medium">
                                Связаться с поддержкой
                            </button>
                        </>
                    )}

                    {(application.status === UserStatus.PENDING_MODERATION ||
                        application.status === UserStatus.APPROVED ||
                        application.status === UserStatus.VERIFIED) && (
                            <button className="flex-1 bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 transition-colors font-medium">
                                Связаться с поддержкой
                            </button>
                        )}

                    {application.status === UserStatus.REJECTED && (
                        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                            <div className="flex items-start space-x-2">
                                <svg className="w-4 h-4 text-yellow-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <p className="text-xs text-yellow-700">
                                    <strong>Внимание:</strong> При повторной подаче заявки ваши данные будут сброшены для заполнения заново
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
